/**
 * Voice note — `docs/activity.md` §11.2, Board 15 A.
 *
 * A full-screen sheet: *Cancel · Voice note*, where it is saving to, the
 * timer (40/700, tabular), *● Recording*, a 46-bar level meter, the words as
 * they are said — final in ink, the one still being heard in faint, a caret
 * — and three controls: **Pause**, **Stop and save** (72 px red, a white
 * square) and **Photo**, which takes a picture without stopping (§11.3).
 *
 * **Stop and save** makes one `note` (`input_mode 'voice'`, the words as its
 * body, the stop it was made at, the stop's job, place and contact) and the
 * recording as an `audio` file on it — both through the upload queue
 * (§11.4), so a note said with no signal is not lost, and the note's client
 * id means a retry never makes two. Ten minutes at most: amber at 9:30,
 * saved at 10:00.
 *
 * Opened from New on a phone (Board 16 D) there is no stop behind it, so
 * *Saving to* offers **Attach** — a note needs somewhere to live (§9.2).
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { uuidv7 } from 'uuidv7'
import type { RecordRef } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { uploadQueue } from '../scope/upload-queue.js'
import { SavingTo, type SaveChip } from '../scope/saving-to.js'
import { AttachSheet } from '../scope/attach-sheet.js'
import { METER_BARS, WARN_AT_SECONDS, useRecorder } from './recorder.js'
import { useBackLayer } from '../use-activity-history.js'

/** One record it goes to; a `label` makes it a chip under *Saving to*, none links it quietly. */
export interface VoiceLink {
  contactId?: string
  propertyId?: string
  jobId?: string
  label?: string
}

/** The page's layer: above Activity, below its own sheets. */
const PAGE_LAYER = 4

/** The waiting bars of Board 15 A — what the meter looks like before anything is said. */
const IDLE = [34, 32, 22, 18, 18, 25, 41, 54, 54, 40, 16, 41, 56, 55, 42, 25]

export function refsOf(links: readonly VoiceLink[]): { refs: RecordRef[]; chips: SaveChip[] } {
  const refs: RecordRef[] = []
  const chips: SaveChip[] = []
  const add = (ref: RecordRef, label?: string) => {
    if (refs.some((one) => one.type === ref.type && one.id === ref.id)) return
    refs.push(ref)
    if (label) chips.push({ ...ref, label })
  }
  for (const link of links) {
    if (link.jobId) add({ type: 'job', id: link.jobId }, link.label)
    if (link.propertyId) add({ type: 'property', id: link.propertyId }, link.label)
    if (link.contactId) add({ type: 'contact', id: link.contactId }, link.label)
  }
  return { refs, chips }
}

const clockOf = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

export function VoiceNoteSheet({
  links = [], madeFromActivityId, onClose, onSaved,
}: {
  links?: VoiceLink[]
  /** The stop it was said at. */
  madeFromActivityId?: string
  onClose: () => void
  /** Queued — it goes up when the phone can send it. */
  onSaved?: () => void
}) {
  const given = useMemo(() => refsOf(links), [links])
  const [attached, setAttached] = useState<SaveChip[]>([])
  const [attaching, setAttaching] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const camera = useRef<HTMLInputElement>(null)
  const batch = useRef(uuidv7())
  const save = useRef<() => void>(() => {})
  const recorder = useRecorder(() => save.current())
  /* The whole screen on a phone: back is its Cancel (Aaron, Oct 26). */
  useBackLayer(onClose)

  const refs = given.refs.length > 0 ? given.refs : attached.map(({ type, id }) => ({ type, id }))
  const chips = given.refs.length > 0 ? given.chips : attached

  save.current = useCallback(async () => {
    if (saving) return
    const words = [recorder.finalText, recorder.interim].filter(Boolean).join(' ').trim()
    if (!words) {
      setProblem(recorder.hearing ? 'Nothing has been typed yet — say it, or Cancel.' : 'Type what to keep, or Cancel.')
      return
    }
    if (refs.length === 0) {
      setProblem('Attach it to a job, a contact or a place first.')
      return
    }
    setSaving(true)
    const made = await recorder.stop()
    const queue = uploadQueue()
    const fileClientIds: string[] = []
    /* Never the recording without the text — it is only ever saved beside its words. */
    if (made.blob && made.text) {
      const extension = (made.contentType ?? '').includes('mp4') ? 'm4a' : 'webm'
      const file = await queue.addFile({
        batch: batch.current, blob: made.blob, name: `Voice note.${extension}`,
        contentType: made.contentType ?? 'audio/webm', uploadKind: 'audio', durationSeconds: made.seconds,
        links: [], type: 'audio',
      })
      fileClientIds.push(file.clientId)
    }
    const [first, ...rest] = refs
    await queue.addActivity({
      batch: batch.current,
      record: first!,
      payload: {
        kind: 'note', body: made.text || words, inputMode: 'voice', links: rest, expand: false,
        ...(madeFromActivityId ? { madeFromActivityId } : {}),
      },
      fileClientIds,
    })
    onSaved?.()
    onClose()
  }, [saving, recorder, refs, madeFromActivityId, onSaved, onClose])

  /* A photo without stopping (§11.3): to the job, from this stop. */
  const takePhotos = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const target = refs.find((ref) => ref.type === 'job') ?? refs[0]
    if (!target) return
    const property = refs.find((ref) => ref.type === 'property')
    const queue = uploadQueue()
    for (const file of Array.from(files)) {
      await queue.addFile({
        batch: batch.current, blob: file, name: file.name || 'Photo.jpg', contentType: file.type || 'image/jpeg',
        uploadKind: 'photo', type: 'photo',
        links: property && property !== target ? [target, property] : [target],
        ...(madeFromActivityId ? { source: { kind: 'activity' as const, id: madeFromActivityId } } : {}),
      })
    }
    if (camera.current) camera.current.value = ''
  }

  const warn = recorder.seconds >= WARN_AT_SECONDS
  const paused = recorder.phase === 'paused'
  const heard = recorder.phase === 'recording' && recorder.keepingSound ? [...recorder.levels, recorder.live] : recorder.levels
  /* What is still to come is the board's quiet grey, its last sixteen bars kept as drawn. */
  const waiting = Math.max(0, METER_BARS - heard.length)
  const idle = [...Array.from({ length: Math.max(0, waiting - IDLE.length) }, (_, index) => IDLE[index % IDLE.length]!),
    ...IDLE.slice(Math.max(0, IDLE.length - waiting))]

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-surface [line-height:normal]" data-overlay-layer={PAGE_LAYER}
      role="dialog" aria-modal="true" aria-label="Voice note">
      <div className="flex h-[calc(53px+env(safe-area-inset-top))] shrink-0 items-center gap-2 border-b border-line bg-surface px-3.5 pt-[env(safe-area-inset-top)]">
        <button type="button" onClick={onClose} className="w-[70px] text-left text-[15px] text-accent">Cancel</button>
        <span className="flex-1" />
        <b className="text-[15.5px]">Voice note</b>
        <span className="flex-1" />
        <span className="w-[70px]" />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-surface px-[18px] py-4">
        <SavingTo chips={chips} onAdd={given.refs.length === 0 ? () => setAttaching(true) : undefined} />
        <div className="h-[22px]" />
        <div className="text-center">
          <b className={cn('text-[40px] tracking-[-.02em] tabular-nums', warn && 'text-warn')}>
            {clockOf(recorder.seconds)}
          </b>
          <div className={cn('mt-0.5 flex items-center justify-center gap-1.5 text-[12.5px] font-semibold',
            paused ? 'text-text-muted' : 'text-danger')}>
            <i aria-hidden className={cn('block h-2 w-2 rounded-full', paused ? 'bg-line-strong' : 'bg-danger')} />
            {paused ? 'Paused' : 'Recording'}
          </div>
        </div>
        <div className="h-3.5" />
        <div aria-hidden className="flex h-16 items-center justify-center gap-[3px]">
          {heard.map((level, index) => (
            <i key={`l${index}`} className="block w-1 rounded-[3px] bg-accent"
              style={{ height: Math.round(16 + level * 48) }} />
          ))}
          {idle.map((height, index) => (
            <i key={`i${index}`} className="block w-1 rounded-[3px]" style={{ height, background: 'rgb(214 213 207)' }} />
          ))}
        </div>
        <div className="h-[18px]" />
        {recorder.hearing ? (
          <div className="min-h-[104px] rounded-xl border border-line-faint bg-surface-alt px-3.5 py-3 text-[14.5px] leading-[1.55] text-text">
            {recorder.finalText}
            {recorder.interim && <span className="text-text-faint">{recorder.finalText ? ' ' : ''}{recorder.interim}</span>}
            {!paused && (
              <span aria-hidden className="ml-0.5 inline-block h-[17px] w-0.5 bg-accent align-middle" />
            )}
          </div>
        ) : (
          <textarea
            value={recorder.finalText}
            onChange={(event) => recorder.setTyped(event.target.value)}
            rows={5}
            placeholder="This phone can’t type as you talk — type the note here."
            className="w-full rounded-xl border border-line-faint bg-surface-alt px-3.5 py-3 text-[14.5px] leading-[1.55] outline-none focus:border-accent"
          />
        )}
        <div className="mt-2 text-center text-[12px] text-text-faint">Typed as you talk — fix any word once it’s saved</div>
        {(problem ?? recorder.problem) && (
          <p role="alert" className="mt-3 text-center text-[12.5px] font-semibold text-danger">{problem ?? recorder.problem}</p>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-center gap-7 border-t border-line bg-surface px-4 pt-3.5 pb-[max(22px,env(safe-area-inset-bottom))]">
        <div className="flex flex-col items-center">
          <button type="button" aria-label={paused ? 'Resume' : 'Pause'}
            onClick={paused ? recorder.resume : recorder.pause}
            className="inline-flex h-[54px] w-[54px] items-center justify-center rounded-full border border-line text-text">
            <Glyph name={paused ? 'play' : 'pause'} size={22} stroke={2.2} />
          </button>
          <span className="mt-1 text-[11.5px] text-text-muted">{paused ? 'Resume' : 'Pause'}</span>
        </div>
        <div className="flex flex-col items-center">
          <button type="button" aria-label="Stop and save" disabled={saving}
            onClick={() => save.current()}
            className="inline-flex h-[72px] w-[72px] items-center justify-center rounded-full bg-danger shadow-[0_0_0_6px_rgb(var(--color-danger-bg))]">
            <i aria-hidden className="block h-6 w-6 rounded-[5px] bg-white" />
          </button>
          <span className="mt-2 text-[11.5px] text-text-muted">Stop and save</span>
        </div>
        <div className="flex flex-col items-center">
          <button type="button" aria-label="Photo" disabled={refs.length === 0}
            onClick={() => camera.current?.click()}
            className="inline-flex h-[54px] w-[54px] items-center justify-center rounded-full border border-line text-text disabled:opacity-50">
            <Glyph name="camera" size={22} />
          </button>
          <span className="mt-1 text-[11.5px] text-text-muted">Photo</span>
          <input ref={camera} type="file" accept="image/*" capture="environment" multiple className="hidden"
            onChange={(event) => void takePhotos(event.target.files)} />
        </div>
      </div>

      {attaching && (
        <AttachSheet onClose={() => setAttaching(false)}
          onPick={(picked) => { setAttached(picked); setAttaching(false); setProblem(null) }} />
      )}
    </div>,
    document.body,
  )
}
